import { takeLatest, put, call, select } from 'redux-saga/effects';
import { checkToken } from '../auth/sagas';
import { refreshToken, refreshTokenError } from '../auth/actions';
import { Urls } from '../../config/urls';
import { getTask, deleteTask, patchTask, postTask } from '../../services';
import {
  UserManagementActionType,
  getMerchantUsersSuccess,
  getMerchantUsersError,
  deleteMerchantUserSuccess,
  deleteMerchantUserError,
  restoreMerchantUserSuccess,
  restoreMerchantUserError,
  swapOwnerRoleSuccess,
  swapOwnerRoleError,
  inviteUserSuccess,
  inviteUserError,
  resendInviteSuccess,
  resendInviteError,
} from './actions';
import { PanelStore } from '../interfaces';

function* getMerchantUsers(action?: { page?: number; pageSize?: number; filters?: { email?: string; name?: string } }) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  let params = `?page=${action?.page || 0}&page_size=${action?.pageSize || 10}&include_deleted=true`;

  if (action?.filters?.email) {
    params += `&email=${encodeURIComponent(action.filters.email)}`;
  }

  if (action?.filters?.name) {
    params += `&name=${encodeURIComponent(action.filters.name)}`;
  }

  const { response, error } = yield call(getTask, {
    url: Urls.api.merchantUsers + params,
    token: state.auth.token,
  });

  if (response) {
    yield put(getMerchantUsersSuccess(response));
  } else {
    yield put(getMerchantUsersError(error));
  }
}

export function* getMerchantUsersSaga() {
  yield takeLatest(UserManagementActionType.GET_MERCHANT_USERS as any, getMerchantUsers);
}

function* deleteMerchantUser(action: { merchantUserId: number }) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { response, error } = yield call(deleteTask, {
    url: `${Urls.api.deleteMerchantUser.replace(':merchantUserId', String(action.merchantUserId))}`,
    token: state.auth.token,
  });

  if (response) {
    yield put(deleteMerchantUserSuccess());
  } else {
    yield put(deleteMerchantUserError(error));
  }
}

export function* deleteMerchantUserSaga() {
  yield takeLatest(UserManagementActionType.DELETE_MERCHANT_USER as any, deleteMerchantUser);
}

function* restoreMerchantUser(action: { merchantUserId: number }) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { response, error } = yield call(patchTask, {
    url: `${Urls.api.restoreMerchantUser.replace(':merchantUserId', String(action.merchantUserId))}`,
    token: state.auth.token,
    body: {},
  });

  if (response) {
    yield put(restoreMerchantUserSuccess());
  } else {
    yield put(restoreMerchantUserError(error));
  }
}

export function* restoreMerchantUserSaga() {
  yield takeLatest(UserManagementActionType.RESTORE_MERCHANT_USER as any, restoreMerchantUser);
}

function* swapOwnerRole(action: { merchantUserId: number }) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { response, error } = yield call(patchTask, {
    url: `${Urls.api.swapOwnerRole.replace(':merchantUserId', String(action.merchantUserId))}`,
    token: state.auth.token,
    body: {},
  });

  if (response) {
    yield put(swapOwnerRoleSuccess());
    // Refresh token to update roles for current user
    const { response: tokenResponse, error: tokenError } = yield call(postTask, {
      url: Urls.api.refreshLogin,
      body: {},
    });

    if (tokenResponse) {
      yield put(refreshToken(tokenResponse));
    } else if (tokenError) {
      yield put(refreshTokenError(tokenError));
    }
  } else {
    yield put(swapOwnerRoleError(error));
  }
}

export function* swapOwnerRoleSaga() {
  yield takeLatest(UserManagementActionType.SWAP_OWNER_ROLE as any, swapOwnerRole);
}

function* inviteUser(action: { fullName: string; email: string; role: string }) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const params = new URLSearchParams({
    email: action.email,
    name: action.fullName,
    role: action.role,
  });
  const urlWithParams = `${Urls.api.inviteUser}?${params.toString()}`;

  const { response, error } = yield call(postTask, {
    url: urlWithParams,
    token: state.auth.token,
    body: {}, 
  });

  if (response) {
    yield put(inviteUserSuccess());
  } else {
    let errorInfo = error;
    if (error && error.body && error.body.errorCode) {
      errorInfo = {
        ...error,
        errorCode: error.body.errorCode,
        errorMessage: error.body.errorMessage,
      };
    }
    yield put(inviteUserError(errorInfo));
  }
}

export function* inviteUserSaga() {
  yield takeLatest(UserManagementActionType.INVITE_USER as any, inviteUser);
}

function* resendInvite(action: { merchantUserId: number }) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { response, error } = yield call(postTask, {
    url: `${Urls.api.resendInvite.replace(':merchantUserId', String(action.merchantUserId))}`,
    token: state.auth.token,
    body: {},
  });

  if (response) {
    yield put(resendInviteSuccess());
  } else {
    yield put(resendInviteError(error));
  }
}

export function* resendInviteSaga() {
  yield takeLatest(UserManagementActionType.RESEND_INVITE as any, resendInvite);
}
